/**
 * API Client for Aura Backend
 * Handles authentication, CSRF tokens, and base request functionality
 */

import axios from 'axios';

// API base URL - use environment variable or fallback to localhost for development
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:10000';

// Helper to get cookie by name
export const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
};

// Default request options
interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
  params?: Record<string, string>;
}

// Error structure from API
interface ApiError {
  detail: string | { msg: string }[];
  status: number;
}

// Main API client function
export const apiClient = async <T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> => {
  const { skipAuth = false, params = {}, ...fetchOptions } = options;
  
  // Build URL with query parameters
  const queryParams = new URLSearchParams(params).toString();
  const baseUrl = endpoint.startsWith('http') ? endpoint : API_BASE_URL;
  const url = `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}${queryParams ? `?${queryParams}` : ''}`;
  
  // Default options for all requests
  const defaultOptions: RequestInit = {
    credentials: 'include', // Include cookies in requests
    mode: 'cors', // Always use CORS mode to ensure proper CORS headers
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...fetchOptions.headers,
    },
  };
  
  // Add CSRF token for non-GET requests if not skipping auth
  if (!skipAuth && fetchOptions.method && fetchOptions.method !== 'GET') {
    const csrfToken = getCookie('csrf_token');
    if (csrfToken) {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        'X-CSRF-Token': csrfToken,
      };
    }
  }
  
  // Merge options
  const requestOptions: RequestInit = {
    ...defaultOptions,
    ...fetchOptions,
    headers: {
      ...defaultOptions.headers,
      ...fetchOptions.headers,
    },
  };
  
  try {
    console.log('Making API request to:', url);
    const response = await fetch(url, requestOptions);
    
    // Handle 401 Unauthorized with automatic token refresh
    if (response.status === 401 && !skipAuth) {
      // We'll implement a refresh token flow here instead of immediately throwing
      // The actual refresh logic is in auth.ts and will be triggered by isLoggedIn()
      // This avoids circular dependencies while still allowing auto-refresh
      
      // For now, just continue with the error
      console.log('Received 401, auth will be refreshed on next attempt');
      throw { 
        detail: 'Session expired. Please try again.',
        status: 401 
      };
    }
    
    // Parse response based on content type
    let data;
    const contentType = response.headers.get('content-type');
    
    try {
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else if (contentType?.includes('text/')) {
        data = await response.text();
        // Try to parse it as JSON if it looks like JSON
        if (typeof data === 'string' && data.trim().startsWith('{')) {
          try {
            data = JSON.parse(data);
          } catch (e) {
            console.log('Text response could not be parsed as JSON:', data);
          }
        }
      } else {
        data = await response.blob();
      }
    } catch (error) {
      console.error('Error parsing response:', error);
      // Return empty object if parsing fails
      data = {};
    }
    
    // Handle error responses
    if (!response.ok) {
      console.error(`API error (${response.status}):`, data);
      throw {
        detail: data.detail || data.message || 'Unknown error occurred',
        status: response.status,
      };
    }
    
    return data as T;
  } catch (error) {
    // Handle network errors or JSON parse errors
    if (!(error as ApiError).status) {
      console.error('API request failed:', error);
      throw {
        detail: 'Network error or server unavailable',
        status: 0,
      };
    }
    
    throw error;
  }
};

// Convenience methods for common HTTP methods
export const get = <T>(endpoint: string, options?: RequestOptions) => 
  apiClient<T>(endpoint, { ...options, method: 'GET' });

export const post = <T>(endpoint: string, data?: any, options?: RequestOptions) => {
  const body = data ? JSON.stringify(data) : undefined;
  console.log(`POST ${endpoint} with body:`, body);
  return apiClient<T>(endpoint, { 
    ...options, 
    method: 'POST',
    body: body,
  });
};

export const put = <T>(endpoint: string, data?: any, options?: RequestOptions) => 
  apiClient<T>(endpoint, { 
    ...options, 
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });

export const patch = <T>(endpoint: string, data?: any, options?: RequestOptions) => 
  apiClient<T>(endpoint, { 
    ...options, 
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });

export const del = <T>(endpoint: string, options?: RequestOptions) => 
  apiClient<T>(endpoint, { ...options, method: 'DELETE' });

/**
 * Post form data with file uploads
 * @param endpoint API endpoint
 * @param formData FormData object containing files/data
 * @param options Additional axios request options
 * @returns Promise with the response data
 */
export const postFormData = async <T>(
  endpoint: string,
  formData: FormData,
  options: Record<string, any> = {}
): Promise<T> => {
  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  
  // Get CSRF token for non-GET requests
  const csrfToken = getCookie('csrf_token');
  
  // Default options for all requests
  const requestOptions = {
    withCredentials: true, // Include cookies for auth
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...options.headers
    },
    ...options
  };
  
  try {
    console.log('Making FormData API request to:', url);
    const response = await axios.post(url, formData, requestOptions);
    return response.data as T;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('API request failed:', error.message);
      throw {
        detail: error.response?.data?.detail || 'Network error or server unavailable',
        status: error.response?.status || 0,
      };
    }
    
    throw error;
  }
};

/**
 * Put form data with file uploads
 * @param endpoint API endpoint
 * @param formData FormData object containing files/data
 * @param options Additional axios request options
 * @returns Promise with the response data
 */
export const putFormData = async <T>(
  endpoint: string,
  formData: FormData,
  options: Record<string, any> = {}
): Promise<T> => {
  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  
  // Get CSRF token for non-GET requests
  const csrfToken = getCookie('csrf_token');
  
  // Default options for all requests
  const requestOptions = {
    withCredentials: true, // Include cookies for auth
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...options.headers
    },
    ...options
  };
  
  try {
    console.log('Making FormData PUT request to:', url);
    const response = await axios.put(url, formData, requestOptions);
    return response.data as T;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('API request failed:', error.message);
      throw {
        detail: error.response?.data?.detail || 'Network error or server unavailable',
        status: error.response?.status || 0,
      };
    }
    
    throw error;
  }
};